import { useState, useEffect, useRef, useCallback } from 'react';
import StorySlide from './StorySlide';
import './StoryViewer.scss';

function StoryViewer({ stories, initialGroupIndex, onClose }) {
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [slideIndex, setSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const longPressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);

  const currentGroup = stories[groupIndex];
  const currentSlide = currentGroup?.slides[slideIndex];

  const goToNextSlide = useCallback(() => {
    if (slideIndex < currentGroup.slides.length - 1) {
      setSlideIndex(slideIndex + 1);
    } else if (groupIndex < stories.length - 1) {
      setGroupIndex(groupIndex + 1);
      setSlideIndex(0);
    } else {
      onClose();
    }
  }, [slideIndex, groupIndex, currentGroup, stories, onClose]);

  const goToPrevSlide = useCallback(() => {
    if (slideIndex > 0) {
      setSlideIndex(slideIndex - 1);
    } else if (groupIndex > 0) {
      const prevGroup = stories[groupIndex - 1];
      setGroupIndex(groupIndex - 1);
      setSlideIndex(prevGroup.slides.length - 1);
    }
  }, [slideIndex, groupIndex, stories]);

  // Auto-advance timer
  useEffect(() => {
    if (paused || !currentSlide) return;

    const timer = setTimeout(() => {
      goToNextSlide();
    }, currentSlide.duration);

    return () => clearTimeout(timer);
  }, [groupIndex, slideIndex, paused, currentSlide, goToNextSlide]);

  // Lock body scroll when viewer is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    isLongPressRef.current = false;

    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      setPaused(true);
    }, 300);
  };

  const handleTouchEnd = (e) => {
    clearTimeout(longPressTimerRef.current);

    if (isLongPressRef.current) {
      setPaused(false);
      isLongPressRef.current = false;
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaTime = Date.now() - touchStartRef.current.time;

    // Swipe detection (horizontal swipe > 50px in < 300ms)
    if (Math.abs(deltaX) > 50 && deltaTime < 300) {
      if (deltaX < 0) {
        // Swipe left -> next group
        if (groupIndex < stories.length - 1) {
          setGroupIndex(groupIndex + 1);
          setSlideIndex(0);
        } else {
          onClose();
        }
      } else {
        // Swipe right -> previous group
        if (groupIndex > 0) {
          const prevGroup = stories[groupIndex - 1];
          setGroupIndex(groupIndex - 1);
          setSlideIndex(prevGroup.slides.length - 1);
        }
      }
      return;
    }

    // Tap detection: left 30% = prev, right 70% = next
    const screenWidth = window.innerWidth;
    if (touch.clientX < screenWidth * 0.3) {
      goToPrevSlide();
    } else {
      goToNextSlide();
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if finger moves
    clearTimeout(longPressTimerRef.current);
  };

  if (!currentGroup || !currentSlide) return null;

  return (
    <div className="story-viewer">
      <div
        className="story-viewer__background"
        style={{ backgroundImage: `url(${currentSlide.backgroundImage})` }}
      />

      <div className="story-viewer__header">
        <div className="story-viewer__progress-bar">
          {currentGroup.slides.map((slide, idx) => {
            let fillClass = 'story-viewer__progress-fill';
            if (idx < slideIndex) {
              fillClass += ' story-viewer__progress-fill--completed';
            } else if (idx === slideIndex) {
              fillClass += ' story-viewer__progress-fill--active';
              if (paused) fillClass += ' story-viewer__progress-fill--paused';
            } else {
              fillClass += ' story-viewer__progress-fill--upcoming';
            }

            return (
              <div key={slide.id} className="story-viewer__progress-segment">
                <div
                  className={fillClass}
                  style={
                    idx === slideIndex
                      ? { '--story-duration': `${slide.duration}ms` }
                      : undefined
                  }
                />
              </div>
            );
          })}
        </div>

        <div className="story-viewer__group-info">
          <div className="story-viewer__group-title">
            <img src={currentGroup.icon} alt={currentGroup.title} />
            <span>{currentGroup.title}</span>
          </div>
          <button
            className="story-viewer__close-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            &times;
          </button>
        </div>
      </div>

      <div
        className="story-viewer__touch-area"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        <div className="story-viewer__touch-left" />
        <div className="story-viewer__touch-right" />
      </div>

      <div className="story-viewer__content">
        <StorySlide slide={currentSlide} onClose={onClose} />
      </div>
    </div>
  );
}

export default StoryViewer;
